export const RULE_VERSION = 'BAKBO_STANDARD_2.0';
export const SOLVER_VERSION = 'exact-and-or-2.0.0';

export const FORMATIONS = Object.freeze({
  MA_SANG_SANG_MA: ['horse', 'elephant', 'elephant', 'horse'],
  SANG_MA_MA_SANG: ['elephant', 'horse', 'horse', 'elephant'],
  MA_SANG_MA_SANG: ['horse', 'elephant', 'horse', 'elephant'],
  SANG_MA_SANG_MA: ['elephant', 'horse', 'elephant', 'horse'],
});

export const RULE_PROFILES = Object.freeze({
  KJA_TOURNAMENT_2024: Object.freeze({
    id: 'KJA_TOURNAMENT_2024',
    allowPass: true,
    materialCounting: true,
    hanBonus: 1.5,
    doublePass: 'MATERIAL',
    bikjang: 'UNDER_30_OR_KING_CAPTURE',
    bikjangResolution: 'MATERIAL',
    repetition: 'THIRD_POSITION_ILLEGAL_UNLESS_BOTH_UNDER_30',
    repetitionResolution: 'MATERIAL',
    perpetualCheck: 'REPETITION_POLICY',
  }),
  JANGGI_TRADITIONAL_2_0: Object.freeze({
    id: 'JANGGI_TRADITIONAL_2_0',
    allowPass: true,
    materialCounting: false,
    hanBonus: 0,
    doublePass: 'DRAW',
    bikjang: 'ALWAYS',
    bikjangResolution: 'DRAW',
    repetition: 'THIRD_POSITION_DRAW',
    repetitionResolution: 'DRAW',
    perpetualCheck: 'DRAW',
  }),
  BAKBO_STANDARD_2_0: Object.freeze({
    id: 'BAKBO_STANDARD_2_0',
    allowPass: true,
    materialCounting: false,
    hanBonus: 0,
    doublePass: 'DEFENDER_ESCAPE',
    bikjang: 'DEFENDER_ESCAPE',
    bikjangResolution: 'DEFENDER_ESCAPE',
    repetition: 'DEFENDER_ESCAPE',
    repetitionResolution: 'DEFENDER_ESCAPE',
    perpetualCheck: 'DEFENDER_ESCAPE',
  }),
});

// Stable, platform-independent digest. This is an identity checksum, not a
// security primitive; keeping the core free of Node-only imports lets the
// browser and server execute the exact same rules module.
const digest = (value) => {
  let a = 0x811c9dc5, b = 0x9e3779b9;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    a = Math.imul(a ^ code, 0x01000193) >>> 0;
    b = Math.imul(b ^ (code + index), 0x85ebca6b) >>> 0;
  }
  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
};

const opposite = (side) => side === 'cho' ? 'han' : 'cho';
const key = ({ file, rank }) => `${file}:${rank}`;
const inside = ({ file, rank }) => file >= 0 && file < 9 && rank >= 0 && rank < 10;
const sameSquare = (a, b) => a.file === b.file && a.rank === b.rank;
const palaceLines = [
  [{ file: 3, rank: 0 }, { file: 4, rank: 1 }, { file: 5, rank: 2 }],
  [{ file: 5, rank: 0 }, { file: 4, rank: 1 }, { file: 3, rank: 2 }],
  [{ file: 3, rank: 7 }, { file: 4, rank: 8 }, { file: 5, rank: 9 }],
  [{ file: 5, rank: 7 }, { file: 4, rank: 8 }, { file: 3, rank: 9 }],
];
const palaceSquares = new Set([
  '3:0', '4:0', '5:0', '3:1', '4:1', '5:1', '3:2', '4:2', '5:2',
  '3:7', '4:7', '5:7', '3:8', '4:8', '5:8', '3:9', '4:9', '5:9',
]);
const palaceFor = (side, square) => {
  const inTop = square.file >= 3 && square.file <= 5 && square.rank >= 0 && square.rank <= 2;
  const inBottom = square.file >= 3 && square.file <= 5 && square.rank >= 7 && square.rank <= 9;
  return side === 'han' ? inTop : inBottom;
};
const palaceEdges = new Set(palaceLines.flatMap((line) => [
  `${key(line[0])}-${key(line[1])}`, `${key(line[1])}-${key(line[0])}`,
  `${key(line[1])}-${key(line[2])}`, `${key(line[2])}-${key(line[1])}`,
]));

const palaceStep = (from, to) => {
  if (!palaceSquares.has(key(from)) || !palaceSquares.has(key(to))) return false;
  const dx = Math.abs(from.file - to.file), dy = Math.abs(from.rank - to.rank);
  return dx + dy === 1 || palaceEdges.has(`${key(from)}-${key(to)}`);
};

export const initialPosition = ({
  choFormation = 'MA_SANG_SANG_MA',
  hanFormation = 'MA_SANG_SANG_MA',
} = {}) => {
  const backRank = (side, rank, kingRank, formationCode) => {
    const prefix = side === 'cho' ? 'c' : 'h';
    const formation = FORMATIONS[formationCode];
    if (!formation) throw new Error(`UNKNOWN_FORMATION:${formationCode}`);
    const order = ['chariot', formation[0], formation[1], 'guard', 'guard', formation[2], formation[3], 'chariot'];
    const files = [0, 1, 2, 3, 5, 6, 7, 8];
    return [
      ...order.map((type, index) => ({ id: `${prefix}-${type}-${index}`, side, type, file: files[index], rank })),
      { id: `${prefix}-king`, side, type: 'king', file: 4, rank: kingRank },
    ];
  };
  return [
    ...backRank('han', 0, 1, hanFormation),
    { id: 'h-cannon-0', side: 'han', type: 'cannon', file: 1, rank: 2 },
    { id: 'h-cannon-1', side: 'han', type: 'cannon', file: 7, rank: 2 },
    ...[0, 2, 4, 6, 8].map((file, index) => ({ id: `h-soldier-${index}`, side: 'han', type: 'soldier', file, rank: 3 })),
    ...[0, 2, 4, 6, 8].map((file, index) => ({ id: `c-soldier-${index}`, side: 'cho', type: 'soldier', file, rank: 6 })),
    { id: 'c-cannon-0', side: 'cho', type: 'cannon', file: 1, rank: 7 },
    { id: 'c-cannon-1', side: 'cho', type: 'cannon', file: 7, rank: 7 },
    ...backRank('cho', 9, 8, choFormation),
  ];
};

const lineMoves = (piece, pieces, mode, directions) => {
  const occupied = new Map(pieces.map((item) => [key(item), item]));
  const result = [];
  for (const squares of directions) {
    let screen = false;
    for (const square of squares) {
      const target = occupied.get(key(square));
      if (mode === 'chariot') {
        if (!target) result.push(square);
        else {
          if (target.side !== piece.side) result.push(square);
          break;
        }
      } else if (!screen) {
        if (target) {
          if (target.type === 'cannon') break;
          screen = true;
        }
      } else if (!target) result.push(square);
      else {
        if (target.side !== piece.side && target.type !== 'cannon') result.push(square);
        break;
      }
    }
  }
  return result;
};

const orthogonalRays = (piece) => [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => {
  const ray = [];
  for (let n = 1; n < 10; n += 1) {
    const square = { file: piece.file + dx * n, rank: piece.rank + dy * n };
    if (!inside(square)) break;
    ray.push(square);
  }
  return ray;
});

const palaceRays = (piece) => palaceLines.flatMap((line) => {
  const index = line.findIndex((square) => sameSquare(square, piece));
  if (index < 0) return [];
  const rays = [];
  if (index > 0) rays.push(line.slice(0, index).reverse());
  if (index < line.length - 1) rays.push(line.slice(index + 1));
  return rays;
});

export function pseudoMovesFor(piece, pieces) {
  const occupied = new Map(pieces.map((item) => [key(item), item]));
  const result = [];
  const push = (square) => {
    if (!inside(square)) return;
    const target = occupied.get(key(square));
    if (!target || target.side !== piece.side) result.push(square);
  };

  if (piece.type === 'king' || piece.type === 'guard') {
    for (let dx = -1; dx <= 1; dx += 1) for (let dy = -1; dy <= 1; dy += 1) {
      const to = { file: piece.file + dx, rank: piece.rank + dy };
      if ((dx || dy) && palaceStep(piece, to)) push(to);
    }
  }

  if (piece.type === 'soldier') {
    const forward = piece.side === 'cho' ? -1 : 1;
    [[-1, 0], [1, 0], [0, forward]].forEach(([dx, dy]) => push({ file: piece.file + dx, rank: piece.rank + dy }));
    [-1, 1].forEach((dx) => {
      const to = { file: piece.file + dx, rank: piece.rank + forward };
      if (palaceStep(piece, to)) push(to);
    });
  }

  if (piece.type === 'horse') {
    const patterns = [
      { leg: [1, 0], destinations: [[2, 1], [2, -1]] }, { leg: [-1, 0], destinations: [[-2, 1], [-2, -1]] },
      { leg: [0, 1], destinations: [[1, 2], [-1, 2]] }, { leg: [0, -1], destinations: [[1, -2], [-1, -2]] },
    ];
    patterns.forEach(({ leg, destinations }) => {
      if (occupied.has(`${piece.file + leg[0]}:${piece.rank + leg[1]}`)) return;
      destinations.forEach(([dx, dy]) => push({ file: piece.file + dx, rank: piece.rank + dy }));
    });
  }

  if (piece.type === 'elephant') {
    const patterns = [
      [[1, 0], [2, 1], [3, 2]], [[1, 0], [2, -1], [3, -2]], [[-1, 0], [-2, 1], [-3, 2]], [[-1, 0], [-2, -1], [-3, -2]],
      [[0, 1], [1, 2], [2, 3]], [[0, 1], [-1, 2], [-2, 3]], [[0, -1], [1, -2], [2, -3]], [[0, -1], [-1, -2], [-2, -3]],
    ];
    patterns.forEach(([first, second, destination]) => {
      const blocked = [first, second].some(([dx, dy]) => occupied.has(`${piece.file + dx}:${piece.rank + dy}`));
      if (!blocked) push({ file: piece.file + destination[0], rank: piece.rank + destination[1] });
    });
  }

  if (piece.type === 'chariot' || piece.type === 'cannon') {
    result.push(...lineMoves(piece, pieces, piece.type, [...orthogonalRays(piece), ...palaceRays(piece)]));
  }
  return dedupeSquares(result);
}

const dedupeSquares = (squares) => [...new Map(squares.map((square) => [key(square), square])).values()];

export const applyMove = (pieces, move) => {
  if (move.isPass) return pieces.map((piece) => ({ ...piece }));
  return pieces
    .filter((piece) => !(piece.file === move.to.file && piece.rank === move.to.rank && piece.id !== move.pieceId))
    .map((piece) => piece.id === move.pieceId ? { ...piece, file: move.to.file, rank: move.to.rank } : { ...piece });
};

export const isInCheck = (side, pieces) => {
  const king = pieces.find((piece) => piece.side === side && piece.type === 'king');
  if (!king) return true;
  return pieces.some((piece) => piece.side === opposite(side) && pseudoMovesFor(piece, pieces).some((square) => sameSquare(square, king)));
};

export const isBikjang = (pieces) => {
  const cho = pieces.find((piece) => piece.side === 'cho' && piece.type === 'king');
  const han = pieces.find((piece) => piece.side === 'han' && piece.type === 'king');
  if (!cho || !han || cho.file !== han.file) return false;
  return !pieces.some((piece) => piece.id !== cho.id && piece.id !== han.id && piece.file === cho.file && piece.rank > Math.min(cho.rank, han.rank) && piece.rank < Math.max(cho.rank, han.rank));
};

export function legalMoves(side, pieces, { allowPass = true, allowPassInBikjang = false } = {}) {
  const moves = [];
  for (const piece of pieces.filter((item) => item.side === side)) {
    for (const to of pseudoMovesFor(piece, pieces)) {
      const target = pieces.find((item) => sameSquare(item, to));
      if (target?.type === 'king') continue;
      const move = { pieceId: piece.id, pieceType: piece.type, from: { file: piece.file, rank: piece.rank }, to, capturedId: target?.id || null };
      if (!isInCheck(side, applyMove(pieces, move))) moves.push(move);
    }
  }
  if (allowPass && (!isInCheck(side, pieces) || (allowPassInBikjang && isBikjang(pieces)))) {
    moves.push({ isPass: true, pieceId: null, pieceType: null, from: null, to: null, capturedId: null });
  }
  return moves;
}

export const isCheckmate = (side, pieces) => isInCheck(side, pieces) && legalMoves(side, pieces, { allowPass: false }).length === 0;

export const positionHash = (pieces, sideToMove) => digest(`${sideToMove}|${pieces
  .map((piece) => `${piece.side[0]}:${piece.type}:${piece.file}:${piece.rank}`)
  .sort().join('|')}`);

export const materialScores = (pieces, { hanBonus = 0 } = {}) => {
  const values = { chariot: 13, cannon: 7, horse: 5, elephant: 3, guard: 3, soldier: 2, king: 0 };
  const scores = { cho: 0, han: hanBonus };
  for (const piece of pieces) scores[piece.side] += values[piece.type] || 0;
  return scores;
};

export const materialResult = (pieces, profile = RULE_PROFILES.KJA_TOURNAMENT_2024) => {
  const scores = materialScores(pieces, profile);
  return {
    scores,
    winner: scores.cho > scores.han ? 'cho' : scores.han > scores.cho ? 'han' : null,
    draw: scores.cho === scores.han,
  };
};

const sameMove = (a, b) => Boolean(a && b && a.isPass === b.isPass && (
  a.isPass || (a.from && b.from && a.to && b.to && a.pieceId === b.pieceId && sameSquare(a.from, b.from) && sameSquare(a.to, b.to))
));

const resolveByProfile = (pieces, profile, reason) => {
  const resolution = reason === 'BIKJANG' ? profile.bikjangResolution
    : reason === 'REPETITION' ? profile.repetitionResolution
      : profile.doublePass;
  if (resolution === 'MATERIAL') {
    const material = materialResult(pieces, profile);
    return { status: 'FINISHED', reason, winner: material.winner, draw: material.draw, scores: material.scores };
  }
  if (resolution === 'DRAW') return { status: 'FINISHED', reason, winner: null, draw: true };
  return { status: 'FINISHED', reason, winner: null, draw: false, defenderEscaped: true };
};

const bikjangEligible = (before, after, move, profile) => {
  if (!isBikjang(after)) return false;
  if (profile.bikjang === 'ALWAYS' || profile.bikjang === 'DEFENDER_ESCAPE') return true;
  if (profile.bikjang !== 'UNDER_30_OR_KING_CAPTURE') return false;
  const scores = materialScores(after);
  const bothUnderThirty = scores.cho < 30 && scores.han < 30;
  const mover = before.find((piece) => piece.id === move.pieceId);
  const captured = move.capturedId ? before.find((piece) => piece.id === move.capturedId) : null;
  return bothUnderThirty || (mover?.type === 'king' && Boolean(captured));
};

export const createGameState = ({
  pieces = initialPosition(), sideToMove = 'cho', ruleProfile = 'KJA_TOURNAMENT_2024',
} = {}) => {
  const profile = RULE_PROFILES[ruleProfile];
  if (!profile) throw new Error(`UNKNOWN_RULE_PROFILE:${ruleProfile}`);
  const position = pieces.map((piece) => ({ ...piece }));
  return {
    pieces: position,
    sideToMove,
    ruleProfile: profile.id,
    status: 'PLAYING',
    result: null,
    pendingBikjang: null,
    history: [{ positionHash: positionHash(position, sideToMove), sideToMove, move: null, gaveCheck: isInCheck(sideToMove, position) }],
  };
};

const positionOccurrences = (history, hash) => history.reduce((count, entry) => count + (entry.positionHash === hash ? 1 : 0), 0);

export const playGameMove = (state, suppliedMove) => {
  if (state.status !== 'PLAYING') return { ok: false, code: 'GAME_NOT_PLAYING', state };
  const profile = RULE_PROFILES[state.ruleProfile];
  if (!profile) return { ok: false, code: 'UNKNOWN_RULE_PROFILE', state };
  const candidates = legalMoves(state.sideToMove, state.pieces, {
    allowPass: profile.allowPass,
    allowPassInBikjang: Boolean(state.pendingBikjang),
  });
  const move = candidates.find((candidate) => sameMove(candidate, suppliedMove));
  if (!move) return { ok: false, code: 'ILLEGAL_MOVE', state };

  const before = state.pieces;
  const pieces = applyMove(before, move);
  const nextSide = opposite(state.sideToMove);
  const hash = positionHash(pieces, nextSide);
  const occurrenceAfterMove = positionOccurrences(state.history, hash) + 1;
  const rawScores = materialScores(pieces);
  const bothUnderThirty = rawScores.cho < 30 && rawScores.han < 30;
  if (profile.repetition === 'THIRD_POSITION_ILLEGAL_UNLESS_BOTH_UNDER_30' && occurrenceAfterMove >= 3 && !bothUnderThirty) {
    return { ok: false, code: 'REPETITION_NOT_ALLOWED', state };
  }

  const gaveCheck = isInCheck(nextSide, pieces);
  const entry = { positionHash: hash, sideToMove: nextSide, move, movedBy: state.sideToMove, gaveCheck, bikjang: isBikjang(pieces) };
  const next = {
    ...state,
    pieces,
    sideToMove: nextSide,
    history: [...state.history, entry],
    pendingBikjang: null,
  };

  if (isCheckmate(nextSide, pieces)) {
    next.status = 'FINISHED';
    next.result = { status: 'FINISHED', reason: 'CHECKMATE', winner: state.sideToMove, draw: false };
    return { ok: true, state: next };
  }

  if (state.pendingBikjang && isBikjang(pieces)) {
    next.status = 'FINISHED';
    next.result = resolveByProfile(pieces, profile, 'BIKJANG');
    return { ok: true, state: next };
  }
  if (bikjangEligible(before, pieces, move, profile)) {
    next.pendingBikjang = { offeredBy: state.sideToMove, offeredTo: nextSide, atPly: next.history.length - 1 };
  }

  const previousMove = state.history.at(-1)?.move;
  if (move.isPass && previousMove?.isPass) {
    next.status = 'FINISHED';
    next.result = resolveByProfile(pieces, profile, 'DOUBLE_PASS');
    return { ok: true, state: next };
  }

  if (occurrenceAfterMove >= 3 && (
    profile.repetition === 'THIRD_POSITION_DRAW' ||
    (profile.repetition === 'THIRD_POSITION_ILLEGAL_UNLESS_BOTH_UNDER_30' && bothUnderThirty)
  )) {
    next.status = 'FINISHED';
    next.result = resolveByProfile(pieces, profile, 'REPETITION');
  }
  return { ok: true, state: next };
};

export function validatePosition(pieces, sideToMove) {
  const errors = [];
  if (!['cho', 'han'].includes(sideToMove)) errors.push('LEGAL_SIDE_TO_MOVE');
  if (!Array.isArray(pieces) || !pieces.length) errors.push('LEGAL_INITIAL_POSITION');
  const occupied = new Set();
  const limits = { king: 1, guard: 2, chariot: 2, cannon: 2, horse: 2, elephant: 2, soldier: 5 };
  for (const side of ['cho', 'han']) {
    for (const [type, limit] of Object.entries(limits)) if (pieces.filter((piece) => piece.side === side && piece.type === type).length > limit) errors.push('LEGAL_PIECE_COUNTS');
    const kings = pieces.filter((piece) => piece.side === side && piece.type === 'king');
    if (kings.length !== 1 || !palaceFor(side, kings[0] || { file: -1, rank: -1 })) errors.push('LEGAL_KING_POSITION');
    if (pieces.some((piece) => piece.side === side && piece.type === 'guard' && !palaceFor(side, piece))) errors.push('LEGAL_GUARD_POSITION');
  }
  for (const piece of pieces) {
    if (!inside(piece) || occupied.has(key(piece))) errors.push('LEGAL_INITIAL_POSITION');
    occupied.add(key(piece));
  }
  if (isInCheck(sideToMove, pieces)) errors.push('START_SIDE_IN_CHECK');
  if (isInCheck(opposite(sideToMove), pieces)) errors.push('START_OPPONENT_IN_CHECK');
  if (isBikjang(pieces)) errors.push('START_BIKJANG');
  return { valid: errors.length === 0, errors: [...new Set(errors)] };
}

const moveCode = (move) => move.isPass ? 'PASS' : `${move.from.file}${move.from.rank}-${move.to.file}${move.to.rank}`;

export function verifyBakbo(specification, { maxNodes = 500000 } = {}) {
  const started = Date.now();
  const pieces = specification.pieces.map((piece) => ({ ...piece }));
  const attacker = specification.attacker;
  const targetMoves = Number(specification.attackerMoves);
  const exact = specification.exact !== false;
  const initial = validatePosition(pieces, attacker);
  if (!initial.valid) return failedReport(specification, initial.errors, started);
  if (!Number.isInteger(targetMoves) || targetMoves < 1 || targetMoves > 12) return failedReport(specification, ['VALID_MOVE_COUNT'], started);

  const stats = { nodes: 0, terminalMates: 0, repetitions: 0, bikjang: 0 };
  const solve = (position, side, remaining, history, requireExact) => {
    stats.nodes += 1;
    if (stats.nodes > maxNodes) throw Object.assign(new Error('SEARCH_LIMIT'), { code: 'SEARCH_LIMIT' });
    const hash = positionHash(position, side);
    if ((history.get(hash) || 0) >= 1) { stats.repetitions += 1; return { win: false, reason: 'REPETITION' }; }
    if (isBikjang(position)) { stats.bikjang += 1; return { win: false, reason: 'BIKJANG' }; }
    const defender = opposite(attacker);
    if (isCheckmate(defender, position)) {
      stats.terminalMates += 1;
      return { win: requireExact ? remaining === 0 : remaining >= 0, terminal: 'CHECKMATE' };
    }
    if (isCheckmate(attacker, position) || remaining <= 0) return { win: false, reason: remaining <= 0 ? 'DEPTH' : 'ATTACKER_MATED' };

    const moves = legalMoves(side, position, { allowPass: true });
    const nextHistory = new Map(history); nextHistory.set(hash, (nextHistory.get(hash) || 0) + 1);
    if (side === attacker) {
      const winners = [];
      for (const move of moves) {
        const child = solve(applyMove(position, move), opposite(side), remaining - 1, nextHistory, requireExact);
        if (child.win) winners.push({ move, child });
      }
      return { win: winners.length > 0, winners, nodeType: 'ATTACKER_OR' };
    }
    const branches = [];
    for (const move of moves) {
      const child = solve(applyMove(position, move), opposite(side), remaining, nextHistory, requireExact);
      branches.push({ move, child });
      if (!child.win) return { win: false, branches, nodeType: 'DEFENDER_AND', refutation: moveCode(move) };
    }
    return { win: branches.length > 0, branches, nodeType: 'DEFENDER_AND' };
  };

  try {
    const shorter = targetMoves > 1 ? solve(pieces, attacker, targetMoves - 1, new Map(), false) : { win: false };
    const tree = solve(pieces, attacker, targetMoves, new Map(), exact);
    const winningFirstMoves = tree.winners?.length || 0;
    const checks = {
      LEGAL_INITIAL_POSITION: true,
      LEGAL_PIECE_COUNTS: true,
      LEGAL_SIDE_TO_MOVE: true,
      FORCED_GOAL_EXISTS: tree.win,
      ALL_DEFENCES_COVERED: tree.win,
      NO_ALTERNATIVE_FIRST_MOVE: winningFirstMoves === 1,
      NO_SHORTER_SOLUTION: !shorter.win,
      // Repetition and bikjang encountered while disproving an alternative attacking
      // move are not defects in the winning proof. A repeated/bikjang branch in an
      // attacker win or defender-all-responses proof is already evaluated as loss by
      // solve(), so a completed proof is necessarily free of those escapes.
      NO_UNINTENDED_DRAW: tree.win,
      EXACT_MOVE_COUNT: tree.win && (exact ? true : !shorter.win),
      SOLUTION_TREE_COMPLETE: tree.win,
    };
    const pass = Object.values(checks).every(Boolean);
    const proof = pass ? compactProof(tree.winners) : null;
    return {
      result: pass ? 'PASS' : 'FAIL', status: pass ? 'ENGINE_VERIFIED' : 'REJECTED',
      ruleVersion: RULE_VERSION, solverVersion: SOLVER_VERSION, positionHash: positionHash(pieces, attacker),
      attacker, attackerMoves: targetMoves, exact, winningFirstMoves, shorterSolutionExists: shorter.win,
      nodesSearched: stats.nodes, terminalMatePositions: stats.terminalMates, repetitionLines: stats.repetitions, bikjangLines: stats.bikjang,
      checks, keyMove: pass ? tree.winners[0].move : null, proof,
      proofHash: proof ? digest(JSON.stringify(proof)) : null,
      elapsedMs: Date.now() - started, verifiedAt: new Date().toISOString(),
    };
  } catch (error) {
    return failedReport(specification, [error.code || 'INCONCLUSIVE'], started, stats.nodes);
  }
}

const compactProof = (winners) => {
  const alternatives = winners.map((entry) => ({
    move: entry.move,
    terminal: entry.child.terminal,
    branches: entry.child.branches?.map((branch) => ({
      move: branch.move,
      continuation: branch.child.winners?.length ? compactProof(branch.child.winners) : { terminal: branch.child.terminal },
    })) || [],
  }));
  // `move` and `branches` retain a canonical line for old consumers; alternatives
  // carries every verified winning attacker move at this exact position.
  return { ...alternatives[0], alternatives };
};

const failedReport = (specification, errors, started, nodes = 0) => ({
  result: errors.includes('SEARCH_LIMIT') ? 'INCONCLUSIVE' : 'FAIL', status: 'REJECTED', ruleVersion: RULE_VERSION, solverVersion: SOLVER_VERSION,
  attacker: specification.attacker, attackerMoves: specification.attackerMoves, errors, nodesSearched: nodes, elapsedMs: Date.now() - started, verifiedAt: new Date().toISOString(),
});
